import React, { useEffect, createElement, Component } from 'react';
import { SEO } from '../components/seo/SEO';
import { BreadcrumbSchema } from '../components/seo/Schema';
import { CheckCircleIcon, CalendarIcon, ClockIcon, UserIcon } from 'lucide-react';
export function BookCall() {
  useEffect(() => {
    // Load booking widget script
    const script = document.createElement('script');
    script.src = 'https://admin.thehireadvantage.co/js/form_embed.js';
    script.type = 'text/javascript';
    document.body.appendChild(script);
    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  const testimonials = [{
    quote: 'It took the headache out of recruiting and gave me my time back. In our first month, we brought on eight new reps. Three months later, six of them are still here and producing. We are now the top office in the company.',
    author: 'Nolan, Market Owner, Elite Windows'
  }, {
    quote: "They helped us hire nearly 100 new sales reps in one season, and many are now among our top performers. I have confidence knowing I don't just have a recruiting vendor. I have a true business partner.",
    author: 'Alden, Executive, Premier Roofing'
  }, {
    quote: 'They helped us tighten our back-end so we could grow based on real, reliable, predictable systems instead of hopes, dreams, and shoestrings. We stopped second-guessing our training and started keeping the right people.',
    author: 'Russell, VP of Sales, Shared Solar Advisors'
  }];
  const HeaderContent = () => <>
      <div className="flex justify-center lg:justify-start mb-6" data-id="element-1294">
        <div className="inline-block bg-slate/10 text-slate px-4 py-2 rounded text-sm font-semibold" data-id="element-1295">
          Strategy Call
        </div>
      </div>

      <h1 className="mb-6 text-center lg:text-left" data-id="element-1296">
        Let's Map Out Your Recruiting System
      </h1>

      <p className="text-body-large mb-8 leading-relaxed text-center lg:text-left" data-id="element-1297">
        This is not a high-pressure sales pitch. It is a working session. We
        will look at your current recruiting, find the holes, and see if the
        HIRE Advantage is the right fix.
      </p>
    </>;
  return <>
      <SEO title="Book a Strategy Call | Free D2D Recruiting Consultation | HIRE" description="Schedule a free 45-60 minute strategy call. We'll audit your recruiting process, identify gaps, and map out a system for consistent hires." canonical="/book-call" ogTitle="Book Your Free D2D Recruiting Strategy Call" ogDescription="Get a free recruiting audit. We'll find the holes in your process and show you how to build a system that delivers consistent hires." keywords="d2d recruiting consultation, sales recruiting strategy call, door to door hiring help, free recruiting audit" data-id="element-1298" />

      <BreadcrumbSchema items={[{
      name: 'Home',
      url: '/'
    }, {
      name: 'Book a Call',
      url: '/book-call'
    }]} data-id="element-1299" />

      <div className="pt-32 pb-16 min-h-screen bg-gray-50 overflow-x-hidden" data-id="element-1300">
        <div className="container-custom px-4 sm:px-6" data-id="element-1301">
          <div className="max-w-6xl mx-auto" data-id="element-1302">
            {/* Mobile Header (Visible only on mobile) */}
            <div className="block lg:hidden mb-8" data-id="element-1303">
              <HeaderContent data-id="element-1304" />
            </div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-1305">
              {/* Left: Content */}
              <div className="w-full order-2 lg:order-1" data-id="element-1306">
                {/* Desktop Header (Visible only on desktop) */}
                <div className="hidden lg:block" data-id="element-1307">
                  <HeaderContent data-id="element-1308" />
                </div>

                {/* What to Expect */}
                <div className="bg-white rounded p-6 sm:p-8 shadow-lg border border-gray-200 mb-8" data-id="element-1309">
                  <h3 className="mb-4 flex items-center" data-id="element-1310">
                    <CalendarIcon className="h-6 w-6 text-slate mr-3 flex-shrink-0" data-id="element-1311" />
                    What Happens On the Call
                  </h3>
                  <p className="mb-6 leading-relaxed" data-id="element-1312">
                    During this session, we will:
                  </p>

                  <ul className="space-y-3 mb-6" data-id="element-1313">
                    {['Audit Your Process: Deep-dive into how you currently hire and train.', 'Find the Leaks: Identify where you are losing reps and money.', 'Build the Map: Outline what the HIRE Advantage would look like for you.', 'Check the Fit: We will give you an honest "yes" or "no" on if we can help.'].map((item, index) => <li key={index} className="flex items-start" data-id="element-1314">
                        <CheckCircleIcon className="h-5 w-5 text-slate flex-shrink-0 mr-3 mt-0.5" data-id="element-1315" />
                        <span className="text-sm" data-id="element-1316">{item}</span>
                      </li>)}
                  </ul>

                  <p className="font-medium" data-id="element-1317">
                    You will walk away knowing exactly what is broken and how to
                    fix it.
                  </p>
                </div>

                {/* Call Details */}
                <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1318">
                  <div className="bg-gray-100 rounded p-4" data-id="element-1319">
                    <ClockIcon className="h-6 w-6 text-slate mb-2" data-id="element-1320" />
                    <p className="text-sm" data-id="element-1321">Duration</p>
                    <p className="font-bold text-navy" data-id="element-1322">45–60 minutes</p>
                  </div>
                  <div className="bg-gray-100 rounded p-4" data-id="element-1323">
                    <UserIcon className="h-6 w-6 text-slate mb-2" data-id="element-1324" />
                    <p className="text-sm" data-id="element-1325">Who Attends</p>
                    <p className="font-bold text-navy" data-id="element-1326">
                      You + Any Partners or Executives
                    </p>
                  </div>
                </div>

                {/* Suggested to Bring */}
                <div className="bg-slate/5 rounded p-6 border border-slate/20 mb-8" data-id="element-1327">
                  <p className="font-semibold text-navy mb-3" data-id="element-1328">Bring:</p>
                  <p data-id="element-1329">Your current team size and growth goals.</p>
                </div>

                {/* Testimonials */}
                <div className="space-y-4 mb-8" data-id="element-1330">
                  {testimonials.map((testimonial, index) => <div key={index} className="bg-navy/5 border-l-4 border-slate p-4 sm:p-6 rounded" data-id="element-1331">
                      <p className="italic mb-2 text-sm" data-id="element-1332">
                        "{testimonial.quote}"
                      </p>
                      <p className="text-sm font-semibold" data-id="element-1333">
                        — {testimonial.author}
                      </p>
                    </div>)}
                </div>

                {/* Important Notes */}
                <div className="bg-white rounded p-6 border-2 border-gray-200 shadow-md" data-id="element-1334">
                  <h3 className="font-semibold mb-4" data-id="element-1335">Important Notes</h3>
                  <ul className="space-y-3" data-id="element-1336">
                    <li className="flex items-start" data-id="element-1337">
                      <span className="text-slate mr-2 flex-shrink-0 mt-1" data-id="element-1338">
                        •
                      </span>
                      We are selective.
                    </li>
                    <li className="flex items-start" data-id="element-1339">
                      <span className="text-slate mr-2 flex-shrink-0 mt-1" data-id="element-1340">
                        •
                      </span>
                      If we aren't the right solution, we will tell you.
                    </li>
                    <li className="flex items-start" data-id="element-1341">
                      <span className="text-slate mr-2 flex-shrink-0 mt-1" data-id="element-1342">
                        •
                      </span>
                      This protects your time and ours.
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right: Booking Widget Embed */}
              <div className="w-full order-1 lg:order-2" data-id="element-1343">
                <div className="bg-white rounded p-6 sm:p-8 shadow-2xl border border-gray-200 lg:sticky lg:top-24" data-id="element-1344">
                  <h3 className="mb-6" data-id="element-1345">Schedule Your Strategy Call</h3>

                  {/* Booking Widget */}
                  <iframe src="https://admin.thehireadvantage.co/widget/booking/AwSHKhv5lDjdvcKrGw82" style={{
                  width: '100%',
                  border: 'none',
                  overflow: 'hidden'
                }} scrolling="no" id="g1yzmQ5zrcbQymQN101p_1768929317758" title="Schedule a Strategy Call with HIRE" data-id="element-1346" />
                </div>
              </div>
            </div>

            {/* Final CTA Below Calendar */}
            <div className="mt-16 max-w-3xl mx-auto text-center" data-id="element-1347">
              <h2 className="mb-6" data-id="element-1348">Ready to Get Clarity?</h2>
              <p className="text-body-large mb-8 leading-relaxed" data-id="element-1349">
                Schedule your strategy call above. Let's build your system.
              </p>
              <a href="/how-it-works" className="inline-block border-2 border-navy text-navy hover:bg-navy hover:text-white font-bold px-8 py-4 rounded transition-colors" data-id="element-1350">
                See How It Works →
              </a>
            </div>
          </div>
        </div>
      </div>
    </>;
}